/**
 * The company typeahead (SA-1456 spec §7.1, §5.4): every party field's input. Its value is
 * `{text, companyId}` and the caller writes both. Nothing here links a company a person did not pick:
 * a match is linked by clicking it, a server suggestion (`suggestion`) by its own button, and the text
 * typed stays as typed. Editing the text after a pick keeps the link until Clear or another pick, so a
 * typo fix does not drop it; emptying the text drops it. A pick whose typed text is not already one of
 * the company's names also stores it as a spelling, so the next person gets it first (the store failing
 * is no reason to unlink).
 *
 * The search never filters by role: one row per firm matters more than a short list, so a customer field
 * finds a company added as a shipper. Matches already holding the field's role sort first.
 *
 * The listbox is the `StationInput` pattern: `role=combobox` on the input, `aria-activedescendant`,
 * arrows move, Enter picks, Escape closes. Like `useAirportSearch`, the result is taken only when it
 * belongs to the text typed now (`asked`), so the debounce window never reads "no match".
 */
import { useId, useRef, useState } from 'react'
import { X } from 'lucide-react'
import { Button, Input, Tip } from '@/components/ui'
import { useAddCompany, useAddSpelling, useCompany, useCompanySearch } from '@/hooks/useCompanies'
import { ApiError } from '@/services/apiClient'
import { usePermissionStore } from '@/stores/permissionStore'
import type { CompanyLink, CompanyMatch, CompanyRef } from '@/types/companies'
import { cn } from '@/utils'
import { foldName as fold } from './companyFold'
import {
  CLEAR_LINK, COULDNT_CHECK, LINKED_FALLBACK, NOT_LINKED, NO_MATCH, SEARCHING,
  addCompany, addFailed, alreadyOnFile, linkSuggestion, roleLabel,
} from './companyText'

const namesOf = (m: CompanyMatch): string[] =>
  [m.name, m.short_name, m.legal_name, m.matched].filter((n): n is string => typeof n === 'string' && n !== '')

/** The muted second line: the short name, or the spelling that matched when it is not the name itself. */
function detailOf(m: CompanyMatch): string | null {
  if (fold(m.matched) !== fold(m.name)) return m.matched
  return m.short_name && fold(m.short_name) !== fold(m.name) ? m.short_name : null
}

/** The matches holding `role` first, each group in the server's order. */
function roleFirst(matches: CompanyMatch[], role?: string): CompanyMatch[] {
  if (!role) return matches
  return [...matches.filter((m) => m.roles.includes(role)), ...matches.filter((m) => !m.roles.includes(role))]
}

const toRef = (c: CompanyRef): CompanyRef => ({ id: c.id, name: c.name, short_name: c.short_name, country: c.country, roles: c.roles })

type Option = { kind: 'match'; match: CompanyMatch } | { kind: 'add' }

export function CompanyInput({ value, onChange, role, label, suggestion = null, disabled = false, pickOnly = false }: {
  value: CompanyLink
  onChange: (next: CompanyLink) => void
  /** The role a company added here takes; matches holding it sort first. */
  role?: string
  label: string
  /** The server's resolve answer for this text (§5.4); offered, never applied. */
  suggestion?: CompanyRef | null
  disabled?: boolean
  /** Pick an existing company only (the Companies page's Merge into…): no "Add" row, and a pick
   * never stores the typed text as a spelling. */
  pickOnly?: boolean
}) {
  const id = useId()
  const listId = `${id}-list`
  const [touched, setTouched] = useState(false)
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(-1)
  const [notice, setNotice] = useState<string | null>(null)
  const [known, setKnown] = useState<Record<string, CompanyRef>>({})
  const latest = useRef(value)
  latest.current = value

  const canAdd = usePermissionStore((s) => s.isLoaded && s.permissions.has('companies:add')) && !pickOnly
  const addCompanyMutation = useAddCompany()
  const addSpelling = useAddSpelling()

  const text = value.text
  const trimmed = text.trim()
  // No role filter: a role filter would hide a company not yet tagged with it.
  const search = useCompanySearch(text, { enabled: touched })
  const settled = search.asked === trimmed
  const searching = touched && trimmed.length >= 2 && (!settled || search.isFetching)
  const failed = touched && settled && search.isError
  const matches = touched && settled && !search.isError ? roleFirst(search.matches, role).slice(0, 5) : []
  const exact = matches.some((m) => namesOf(m).some((n) => fold(n) === fold(trimmed)))
  const showAdd = canAdd && trimmed.length >= 2 && touched && settled && !searching && !search.isError && !exact
  const options: Option[] = [...matches.map((match): Option => ({ kind: 'match', match })), ...(showAdd ? [{ kind: 'add' } as Option] : [])]
  const listed = open && options.length > 0

  const linkedId = value.companyId
  const remembered = linkedId ? known[linkedId] : undefined
  const fetched = useCompany(linkedId && !remembered ? linkedId : null)
  const linked = remembered ?? (fetched.data ? toRef(fetched.data) : null)

  const link = (company: CompanyRef, text: string) => {
    setKnown((k) => ({ ...k, [company.id]: toRef(company) }))
    setTouched(false); setOpen(false); setActive(-1)
    onChange({ text, companyId: company.id })
  }

  async function pick(m: CompanyMatch) {
    setNotice(null)
    const typed = latest.current.text
    const wanted = fold(typed)
    // (a) one of its names: keep the text, nothing to store. (b) the start of its name or of the spelling
    // that matched: the person was searching, so the field takes the name. (c) anything else is a
    // spelling worth keeping, for someone who may add one.
    if (namesOf(m).some((n) => fold(n) === wanted)) { link(m, typed); return }
    if (wanted && [m.name, m.matched].some((n) => fold(n).startsWith(wanted))) { link(m, m.name); return }
    link(m, typed)
    if (!canAdd || !wanted) return
    try {
      await addSpelling.mutateAsync({ id: m.id, text: typed.trim() })
    } catch {
      // The spelling is a convenience; the link stands.
    }
  }

  const adding = useRef(false)
  async function add() {
    if (adding.current) return
    adding.current = true
    const name = trimmed
    setNotice(null)
    try {
      const made = await addCompanyMutation.mutateAsync({ name, roles: role ? [role] : undefined })
      link(made, latest.current.text)
    } catch (error) {
      const detail = error instanceof ApiError ? (error.detail as { code?: string; company?: CompanyRef } | null) : null
      if (error instanceof ApiError && error.status === 409 && detail?.code === 'company_exists' && detail.company) {
        link(detail.company, latest.current.text)
        setNotice(alreadyOnFile(detail.company.name))
      } else {
        setNotice(addFailed)
      }
    } finally {
      adding.current = false
    }
  }

  const choose = (option: Option) => { void (option.kind === 'match' ? pick(option.match) : add()) }

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      if (options.length === 0) return
      e.preventDefault()
      setOpen(true)
      setActive((i) => (e.key === 'ArrowDown' ? Math.min(i + 1, options.length - 1) : i <= 0 ? 0 : i - 1))
    } else if (e.key === 'Enter' && listed && active >= 0 && options[active]) {
      e.preventDefault()
      choose(options[active])
    } else if (e.key === 'Escape' && open) {
      e.stopPropagation()
      setOpen(false); setActive(-1)
    }
  }

  const statusText = notice
    ?? (failed ? COULDNT_CHECK
      : searching && open ? SEARCHING
        : open && touched && settled && trimmed.length >= 2 && options.length === 0 ? NO_MATCH : '')

  const optionId = (i: number) => `${id}-opt-${i}`

  return (
    <div className="relative space-y-1">
      <Input
        id={id} label={label} role="combobox" autoComplete="off" disabled={disabled} value={text}
        aria-expanded={listed} aria-controls={listed ? listId : undefined} aria-autocomplete="list"
        aria-activedescendant={listed && active >= 0 ? optionId(active) : undefined}
        onChange={(e) => {
          setTouched(true); setOpen(true); setActive(-1); setNotice(null)
          // An emptied field links nothing; a typo fix keeps the link (§7.1), and a later pick links again.
          onChange({ text: e.target.value, companyId: e.target.value.trim() ? latest.current.companyId : null })
        }}
        onKeyDown={onKeyDown}
        onBlur={() => { setOpen(false); setActive(-1) }}
      />
      {listed && (
        <ul id={listId} role="listbox" aria-label={label}
          className="absolute z-20 mt-1 w-full overflow-hidden rounded-md border border-border bg-card shadow-md">
          {options.map((option, i) => (
            <li key={option.kind === 'match' ? option.match.id : 'add'} id={optionId(i)} role="option" aria-selected={i === active}
              onMouseDown={(e) => e.preventDefault()} onClick={() => choose(option)} onMouseEnter={() => setActive(i)}
              className={cn('flex cursor-pointer flex-wrap items-center gap-x-2 gap-y-0.5 border-l-2 px-2.5 py-1.5 text-sm',
                i === active ? 'border-l-primary bg-accent' : 'border-l-transparent')}>
              {option.kind === 'match' ? (
                <>
                  <span className="font-medium">{option.match.name}</span>
                  {detailOf(option.match) && <span className="text-xs text-muted-foreground">{detailOf(option.match)}</span>}
                  {option.match.roles.map((r) => (
                    <span key={r} className="rounded border border-border px-1.5 text-[11px] text-muted-foreground">{roleLabel(r)}</span>
                  ))}
                </>
              ) : (
                <span>{addCompany(trimmed)}</span>
              )}
            </li>
          ))}
        </ul>
      )}
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
        {linkedId ? (
          <span data-testid="company-chip" className="inline-flex items-center gap-1 rounded-md border border-border bg-muted px-2 py-0.5 text-foreground">
            <span>{linked?.name ?? LINKED_FALLBACK}</span>
            <Tip text={CLEAR_LINK}>
              <Button type="button" variant="ghost" size="sm" aria-label={CLEAR_LINK} disabled={disabled}
                className="h-5 w-5 p-0 text-muted-foreground"
                onClick={() => { setNotice(null); onChange({ text: latest.current.text, companyId: null }) }}>
                <X className="size-3.5" aria-hidden="true" />
              </Button>
            </Tip>
          </span>
        ) : trimmed ? (
          <>
            <span>{NOT_LINKED}</span>
            {suggestion && (
              <Button type="button" variant="outline" size="sm" disabled={disabled} className="h-6 px-2 text-xs"
                onClick={() => { setNotice(null); link(suggestion, latest.current.text) }}>
                {linkSuggestion(suggestion.name)}
              </Button>
            )}
          </>
        ) : null}
        <span role="status">{statusText}</span>
      </div>
    </div>
  )
}
